/**
 * The meta line under a list's row on the Lists index, and the Shortlist's
 * own. Pure and dependency-free (only type-only imports, erased at compile
 * time) so `test/list-summary.test.ts` runs under `node --test` without
 * pulling in Supabase or React — the same reason `listParse.ts` and
 * `aisles.ts` are shaped this way.
 *
 * `FeedRow` and `Commitment` are read as plain shapes rather than imported
 * from `entries.ts`: that file has its own extensionless internal imports,
 * which Vite resolves and Node's ESM resolver does not (see
 * `docs/app-plan/README.md`'s note on `deriveFeed`'s own test). `idsOf` is
 * copied rather than imported for the same reason — it is three lines.
 */
import type { Commitment, FeedRow, List, ListItem, ListRun, PurchaseStatus } from './types'
import { iso as isoDay } from './calendar.ts'
import { hereZone } from './zone.ts'
import { priceSymbol } from './currency.ts'
import { byAddedDesc } from './newPosition.ts'

const MS_DAY = 86_400_000
const MS_WEEK = 7 * MS_DAY

/**
 * "2 Oct", for a run's `completed_at` — or "just now" when there is none to
 * read (a2-46). A run the client has only just written can be in the cache
 * without its timestamp (the insert's echo, before the refetch), and
 * `new Date(undefined)` printed "Last run Invalid Date" on the checklist
 * and the index row until it arrived. A run with no time yet is the one
 * that just happened. Shared with `Checklist.tsx`, which had its own copy.
 */
export function shortDate(iso: string | null | undefined): string {
  const t = iso ? Date.parse(iso) : NaN
  if (Number.isNaN(t)) return 'just now'
  // The current region's day (C2.1): a run is the person's.
  return new Date(t).toLocaleDateString('en-GB', { timeZone: hereZone(), day: 'numeric', month: 'short' })
}

/** The most recent run, with a run that has no `completed_at` yet counted as
 *  the newest — it is the one the client just wrote — rather than sorted by
 *  comparing `undefined` with a string, which put it anywhere. */
export function lastRunOf<R extends Pick<ListRun, 'completed_at'>>(runs: R[]): R | undefined {
  const at = (r: R) => r.completed_at || '\uffff'
  return [...runs].sort((a, b) => (at(a) < at(b) ? 1 : at(a) > at(b) ? -1 : 0))[0]
}

/** Every entry id a card stands for — copied from `entries.ts`'s `idsOf`,
 *  which a merged film card needs and a saved concert never has. */
const idsOf = (e: FeedRow): string[] => e.group_ids ?? [e.id]

/**
 * A purchase's status as every screen reads it. An item written before the
 * column existed, or added by the paste path, has no `status` at all, and the
 * Purchases list has always drawn that row under Considering — so null *is*
 * considering, here and in `PurchaseList.tsx`, which imports this. The index
 * used to count only a literal `'considering'`, and said "0 considering" over
 * a list showing fifteen (UI-audit a2-01): one null, read as "considering" on
 * one screen and as "nothing" on the other.
 */
export const purchaseStatusOf = (i: Pick<ListItem, 'status'>): PurchaseStatus => i.status ?? 'considering'

/**
 * What a set of purchases adds up to, per currency, largest first; empty when
 * nothing is priced. An item keeps the currency it was priced in (C2.3: a new
 * price defaults to the current region's, an old one is not rewritten), so a
 * list holding both euros and dollars has two totals and **no sum across
 * them** — 25 dollars added to 100 euros is a number that means nothing.
 */
export function totalsByCurrency(
  items: readonly Pick<ListItem, 'price_cents' | 'currency'>[],
): { currency: string; cents: number }[] {
  const by = new Map<string, number>()
  for (const i of items) {
    if (!i.price_cents || i.price_cents <= 0) continue
    by.set(i.currency, (by.get(i.currency) ?? 0) + i.price_cents)
  }
  return [...by].map(([currency, cents]) => ({ currency, cents })).sort((a, b) => b.cents - a.cents)
}

/**
 * The shop a link is on, for a purchase or a collection row (a2-21): its
 * hostname with a leading `www.` dropped — `amazon.de`, `ikea.com`. Empty
 * for no link or one that is not a URL (a pasted title, a typo), which the
 * row reads as "nothing to say" rather than drawing a placeholder.
 */
export function shopOf(link: string | null | undefined): string {
  if (!link) return ''
  try { return new URL(link).hostname.replace(/^www\./i, '') } catch { return '' }
}

export interface ListSummary {
  meta: string
  /** Template only: the leading slot draws this as a ring instead of the
   *  kind's icon. */
  ring?: { done: number; total: number }
}

/**
 * One list's row, summarised for the index (L1's table).
 *
 * `now` is a parameter rather than `new Date()` read inside, so a test can
 * fix "today" instead of racing the clock — the same reason `alreadyHappened`
 * in `entries.ts` takes `today` as an argument.
 */
export function listSummary(list: List, items: ListItem[], runs: ListRun[], now: Date): ListSummary {
  const open = items.filter(i => !i.done_at)
  const done = items.filter(i => i.done_at)

  if (list.kind === 'task') {
    const today = isoDay(now)
    const dueToday = open.filter(i => i.due_on === today).length
    return { meta: `${dueToday} due today · ${open.length} open` }
  }

  if (list.kind === 'template') {
    const lastRun = lastRunOf(runs)
    const when = lastRun ? shortDate(lastRun.completed_at) : 'never'
    return {
      meta: `Checklist · last run ${when}`,
      ring: { done: done.length, total: items.length },
    }
  }

  if (list.kind === 'grocery') {
    const bodies = open.slice(0, 3).map(i => i.body).join(', ')
    return { meta: bodies ? `${open.length} to get · ${bodies}` : `${open.length} to get` }
  }

  if (list.kind === 'buy') {
    const considering = items.filter(i => purchaseStatusOf(i) === 'considering').length
    const ordered = items.filter(i => purchaseStatusOf(i) === 'ordered').length
    // Only what has not been bought yet — a bought item is spent money, not
    // money the meta line is warning about.
    const pending = items.filter(i => purchaseStatusOf(i) !== 'bought')
    const totals = totalsByCurrency(pending)
    const spend = totals.length
      ? ` · ~${totals.map(t => `${priceSymbol(t.currency)}${Math.round(t.cents / 100)}`).join(' + ')}`
      : ''
    return { meta: `${considering} considering · ${ordered} ordered${spend}` }
  }

  // collection
  if (list.flag_label) {
    const flagged = items.filter(i => i.flagged_at).length
    return { meta: `${items.length} · ${flagged} ${list.flag_label}` }
  }
  const lastAdded = [...items].sort(byAddedDesc)[0] ?? null
  return { meta: lastAdded ? `${items.length} · last added ${lastAdded.body}` : `${items.length}` }
}

/**
 * The Shortlist row's meta: `N saved · N this week`.
 *
 * There used to be a third clause, `N needs a date`. It truncated at 390px and
 * its wording disagreed with the Shortlist screen's own "pick a night"; a row
 * with no `run_start` is simply never "this week". "This week" prefers a commitment's own `starts_at`
 * (E1's chip, or a manual pick) over the feed's `next_showing`, over
 * `run_start` — the same order `EventSheet.tsx` reads a commitment in,
 * because a saved run's earliest showing is not always the one actually in
 * the diary.
 */
export function shortlistSummary(saved: FeedRow[], commitments: Commitment[], now: Date): string {
  const weekAhead = now.getTime() + MS_WEEK
  let thisWeek = 0

  for (const e of saved) {
    if (!e.run_start) continue
    const ids = idsOf(e)
    const committed = commitments.find(c => c.entry_id && ids.includes(c.entry_id))
    const when = committed?.starts_at ?? committed?.on_date ?? e.next_showing ?? e.run_start
    const t = Date.parse(when)
    if (!Number.isNaN(t) && t >= now.getTime() && t <= weekAhead) thisWeek++
  }

  return `${saved.length} saved · ${thisWeek} this week`
}

/**
 * The number after a list's name on its chip (Lists, direction B). The chip
 * is one line, so this is the one figure the kind is about, not the row's
 * whole sentence: what is still to do (Tasks, Groceries), what has not been
 * decided (Purchases), how many are kept (a Collection). `null` draws
 * nothing: a zero says "nothing here" by being absent, the rule `ui/Chip`'s
 * own `count` already follows, and a checklist's ring says its progress
 * where a number would only repeat it.
 *
 * Purchases counts *considering* through `purchaseStatusOf`, so a row with no
 * status counts the way the Purchases list and the index meta both draw it
 * (a2-01); an ordered or bought item is not something left to decide.
 */
export function listChipCount(list: List, items: ListItem[]): number | null {
  let n = 0
  if (list.kind === 'task' || list.kind === 'grocery') n = items.filter(i => !i.done_at).length
  else if (list.kind === 'buy') n = items.filter(i => purchaseStatusOf(i) === 'considering').length
  else if (list.kind === 'collection') n = items.length
  return n > 0 ? n : null
}
